"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateBankAccountSchema = exports.createBankAccountSchema = exports.firmVatPeriodSchema = exports.updateFirmSchema = exports.createFirmSchema = void 0;
exports.checkFirmBusinessRules = checkFirmBusinessRules;
const zod_1 = require("zod");
const constants_1 = require("../constants");
const enums_1 = require("../enums");
const validators_1 = require("../validators");
const TAX_REGIME_VALUES = Object.values(enums_1.TaxRegime);
/**
 * CUI travels through the API as digits only (no "RO" prefix, no
 * whitespace) — the transform below normalizes whatever the client typed
 * ("RO 18547290", "ro18547290", "18547290") into that canonical form before
 * the checksum is validated, so what gets persisted is always clean.
 */
const cuiField = zod_1.z
    .string()
    .trim()
    .transform((value) => value.toUpperCase().replace(/^RO/, '').replace(/\s+/g, ''))
    .refine(validators_1.isValidCui, 'CUI invalid.');
const ibanField = zod_1.z
    .string()
    .trim()
    .transform((value) => value.toUpperCase().replace(/\s+/g, ''))
    .refine(validators_1.isValidIban, 'IBAN invalid.');
const decimalStringField = zod_1.z
    .string()
    .trim()
    .regex(/^\d+(\.\d{1,2})?$/, 'Valoarea trebuie să fie un număr pozitiv cu cel mult 2 zecimale.');
/**
 * Cross-field business rules for a firm, as plain data (no zod dependency) —
 * called both from the zod schemas below (create: always, since countyCode/
 * cityName are required; update: only when both happen to be present in the
 * same payload, as a fast pre-check) AND from FirmsService.update(), which
 * re-runs this against the row merged with the persisted state so a PATCH
 * that only touches one of the two fields can't leave county/city
 * inconsistent (a partial payload's schema alone can't see the other
 * field's persisted value).
 */
function checkFirmBusinessRules(data) {
    const issues = [];
    if (!(0, constants_1.isCountyCityValid)(data.countyCode, data.cityName)) {
        issues.push({ field: 'cityName', message: constants_1.BUCHAREST_SECTOR_MESSAGE });
    }
    return issues;
}
function addIssues(issues, ctx) {
    for (const issue of issues) {
        ctx.addIssue({ code: 'custom', path: [issue.field], message: issue.message });
    }
}
// Bare (undefaulted) field definitions, shared by create and update. Zod's
// `.default()` fires on ANY `undefined` input — including a field simply
// absent from a PATCH body — even after `.partial()`; applying `.default()`
// here and reusing this object for `updateFirmSchema.partial()` would mean
// a PATCH `{name: "X"}` alone silently resets countryCode/defaultDueDays/
// defaultCurrency/isActive back to their create-time defaults on every
// update. Defaults are therefore only layered on in `createFirmSchema`
// below, never on the shape update reuses (same pattern as
// series.schema.ts's `seriesFields`).
const firmFields = {
    name: zod_1.z.string().trim().min(1, 'Numele este obligatoriu.'),
    cui: cuiField,
    regCom: zod_1.z.string().trim().min(1).optional(),
    addressLine: zod_1.z.string().trim().min(1, 'Adresa este obligatorie.'),
    cityName: zod_1.z.string().trim().min(1, 'Localitatea este obligatorie.'),
    countyCode: zod_1.z.enum(constants_1.RO_COUNTY_CODES, 'Județ invalid.'),
    postalCode: zod_1.z.string().trim().min(1).optional(),
    countryCode: zod_1.z.string().trim().length(2, 'Codul de țară trebuie să aibă 2 caractere.'),
    email: zod_1.z.email('Email invalid.').optional(),
    phone: zod_1.z.string().trim().min(1).optional(),
    capitalSocial: decimalStringField.optional(),
    defaultDueDays: zod_1.z.coerce.number().int().min(0),
    defaultCurrency: zod_1.z.string().trim().length(3, 'Moneda trebuie să aibă 3 caractere.'),
    defaultInvoiceNotes: zod_1.z.string().trim().optional(),
    isActive: zod_1.z.boolean(),
    /** Regimul de impozitare al firmei — baza estimării de profit net din
     * dashboard (widgetul Profit). */
    taxRegime: zod_1.z.enum(TAX_REGIME_VALUES, 'Regim fiscal invalid.'),
};
exports.createFirmSchema = zod_1.z
    .object({
    ...firmFields,
    countryCode: firmFields.countryCode.default('RO'),
    defaultDueDays: firmFields.defaultDueDays.default(30),
    defaultCurrency: firmFields.defaultCurrency.default('RON'),
    isActive: firmFields.isActive.default(true),
    taxRegime: firmFields.taxRegime.default('MICRO_1'),
})
    .superRefine((data, ctx) => {
    addIssues(checkFirmBusinessRules(data), ctx);
});
// Built from the undefaulted `firmFields` (see comment above) so an omitted
// field stays truly absent instead of reappearing as its create-time
// default. The superRefine here is a fast pre-check only: it catches an
// internally-inconsistent payload that sets both fields at once. It can't
// catch a payload that only changes one of them against a persisted state
// it can't see — FirmsService.update() is the authoritative check for that
// (see checkFirmBusinessRules's doc).
exports.updateFirmSchema = zod_1.z
    .object(firmFields)
    .partial()
    .superRefine((data, ctx) => {
    if (data.countyCode !== undefined && data.cityName !== undefined) {
        addIssues(checkFirmBusinessRules({ countyCode: data.countyCode, cityName: data.cityName }), ctx);
    }
});
exports.firmVatPeriodSchema = zod_1.z
    .object({
    startsOn: zod_1.z.coerce.date('Data de început este obligatorie.'),
    endsOn: zod_1.z.coerce.date().nullable().optional(),
})
    .refine((data) => !data.endsOn || data.endsOn >= data.startsOn, {
    error: 'Data de sfârșit nu poate fi înainte de data de început.',
    path: ['endsOn'],
});
// Same undefaulted-base-fields reasoning as firmFields above: without it, a
// PATCH /bank-accounts/:id {bankName: "X"} would silently reset currency
// back to 'RON' and — worse — isDefault back to false, quietly stripping
// the "this is the default account" flag off a bank account whose owner
// only meant to rename it.
const bankAccountFields = {
    bankName: zod_1.z.string().trim().min(1, 'Numele băncii este obligatoriu.'),
    iban: ibanField,
    // BIC/SWIFT — optional; 8 or 11 alphanumerics. Emitted as BT-86 in the
    // e-Factura PaymentMeans when present.
    bic: zod_1.z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9]{8}([A-Za-z0-9]{3})?$/, 'BIC invalid (8 sau 11 caractere).')
        .transform((value) => value.toUpperCase())
        .optional(),
    currency: zod_1.z.string().trim().length(3, 'Moneda trebuie să aibă 3 caractere.'),
    isDefault: zod_1.z.boolean(),
};
exports.createBankAccountSchema = zod_1.z.object({
    ...bankAccountFields,
    currency: bankAccountFields.currency.default('RON'),
    isDefault: bankAccountFields.isDefault.default(false),
});
exports.updateBankAccountSchema = zod_1.z.object(bankAccountFields).partial();
